import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, Clock, CheckCircle2, Phone, ArrowRight } from 'lucide-react';

const WhatsAppSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const isBusinessHours = () => {
    const hour = currentTime.getHours();
    const day = currentTime.getDay();
    return day >= 1 && day <= 6 && hour >= 8 && hour <= 18; // Monday to Saturday, 8 AM to 6 PM
  };

  const features = [
    {
      icon: Clock,
      title: 'Réponse rapide',
      description: 'En moins de 5 minutes',
      color: 'text-blue-600 bg-blue-100'
    },
    {
      icon: CheckCircle2,
      title: 'Service garanti',
      description: 'Qualité certifiée',
      color: 'text-green-600 bg-green-100'
    },
    {
      icon: Phone,
      title: 'Support dédié',
      description: 'Équipe experte',
      color: 'text-purple-600 bg-purple-100'
    }
  ];

  return (
    <section 
      ref={sectionRef}
      className="py-16 bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 relative overflow-hidden"
      itemScope
      itemType="https://schema.org/ContactPoint"
    >
      {/* Background Decoration */}
      <div className="absolute inset-0">
        {/* Animated circles */}
        <div className="absolute top-10 left-10 w-20 h-20 bg-green-200/30 rounded-full animate-pulse" />
        <div className="absolute bottom-10 right-10 w-32 h-32 bg-emerald-200/30 rounded-full animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-teal-200/30 rounded-full animate-bounce" style={{ animationDelay: '2s' }} />
        
        {/* WhatsApp pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="grid grid-cols-8 gap-8 transform rotate-12 scale-150">
            {[...Array(64)].map((_, i) => (
              <MessageCircle key={i} className="w-6 h-6 text-green-600" />
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Main Card */}
        <div 
          className={`
            relative transition-all duration-1000 transform
            ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}
          `}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <div className={`
            bg-white/90 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/20
            transition-all duration-500 transform
            ${isHovered ? 'scale-105 shadow-3xl' : ''}
          `}>
            
            {/* Status Bar */}
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-t-3xl px-6 py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`w-3 h-3 rounded-full ${isBusinessHours() ? 'bg-green-300' : 'bg-yellow-300'} animate-pulse`} />
                  <span className="text-white font-medium">
                    {isBusinessHours() ? 'En ligne maintenant' : 'Nous répondons bientôt'}
                  </span>
                </div>
                <div className="text-white/80 text-sm">
                  {currentTime.toLocaleTimeString('fr-FR', { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </div>
              </div>
            </div>

            <div className="p-8 lg:p-12">
              <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                
                {/* Content */}
                <div className="space-y-6">
                  
                  {/* Header */}
                  <div className="space-y-4">
                    <div className="inline-flex items-center px-4 py-2 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Contact instantané
                    </div>
                    
                    <h2 
                      className="text-3xl lg:text-4xl font-bold text-gray-900 leading-tight"
                      itemProp="name"
                    >
                      Une question ?<br />
                      <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-emerald-600">
                        Écrivez-nous !
                      </span>
                    </h2>
                    
                    <p 
                      className="text-lg text-gray-600 leading-relaxed"
                      itemProp="description"
                    >
                      Notre équipe vous répond rapidement sur WhatsApp pour toutes vos questions 
                      sur nos services automobiles.
                    </p>
                  </div>

                  {/* Features */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {features.map((feature, index) => {
                      const Icon = feature.icon;
                      return (
                        <div 
                          key={index}
                          className={`
                            p-4 rounded-xl border border-gray-100 bg-white/50
                            transition-all duration-500 transform hover:scale-105
                            ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}
                          `}
                          style={{ transitionDelay: `${(index + 1) * 200}ms` }}
                        >
                          <div className={`inline-flex p-2 rounded-lg ${feature.color} mb-3`}>
                            <Icon className="w-5 h-5" />
                          </div>
                          <h3 className="font-semibold text-gray-900 mb-1">{feature.title}</h3>
                          <p className="text-sm text-gray-600">{feature.description}</p>
                        </div>
                      );
                    })}
                  </div>

                  {/* CTA Buttons */}
                  <div className="flex flex-col sm:flex-row gap-4">
                    <a
                      href="https://wa.me/212702094593"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`
                        group flex-1 inline-flex items-center justify-center px-8 py-4
                        bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold rounded-xl
                        transition-all duration-300 transform hover:scale-105 hover:shadow-lg
                        ${isHovered ? 'shadow-2xl' : ''}
                      `}
                      itemProp="url"
                    >
                      <MessageCircle className="w-5 h-5 mr-3 transition-transform group-hover:scale-110" />
                      <span>Ouvrir WhatsApp</span>
                      <ArrowRight className="w-5 h-5 ml-3 transition-transform group-hover:translate-x-1" />
                    </a>
                    
                    <a
                      href="tel:+212702094593"
                      className="inline-flex items-center justify-center px-6 py-4 border-2 border-green-600 text-green-600 font-semibold rounded-xl hover:bg-green-600 hover:text-white transition-all duration-300"
                    >
                      <Phone className="w-5 h-5 mr-2" />
                      Appeler
                    </a>
                  </div>

                  {/* Trust indicators */}
                  <div className="flex items-center space-x-6 pt-4 border-t border-gray-200">
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      <span>Réponse garantie</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      <span>Service 7j/7</span>
                    </div>
                  </div>
                </div>

                {/* Visual */}
                <div className={`
                  relative transition-all duration-700 transform
                  ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}
                `}>
                  
                  {/* Phone Mockup */}
                  <div className="relative mx-auto max-w-sm">
                    
                    {/* Phone Frame */}
                    <div className="relative bg-gray-900 rounded-3xl p-2 shadow-2xl">
                      <div className="bg-white rounded-2xl overflow-hidden">
                        
                        {/* WhatsApp Header */}
                        <div className="bg-green-500 px-4 py-3 flex items-center space-x-3">
                          <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center">
                            <img 
                              src="/logo.jpeg" 
                              alt="Faucon Bleu" 
                              className="w-6 h-6"
                              style={{objectFit:'center'}}
                              loading="lazy"
                            />
                          </div>
                          <div>
                            <div className="text-white font-semibold text-sm">Faucon Bleu</div>
                            <div className="text-green-100 text-xs">En ligne</div>
                          </div>
                        </div>

                        {/* Chat Messages */}
                        <div className="p-4 space-y-3 h-64 bg-green-50">
                          
                          {/* Incoming message */}
                          <div className="flex space-x-2">
                            <div className="bg-white rounded-2xl rounded-bl-sm px-3 py-2 max-w-xs shadow-sm">
                              <p className="text-sm text-gray-800">
                                Bonjour ! 👋 Comment puis-je vous aider pour l'entretien de votre véhicule ?
                              </p>
                              <div className="text-xs text-gray-500 mt-1">09:30</div>
                            </div>
                          </div>

                          {/* Outgoing message */}
                          <div className="flex justify-end space-x-2">
                            <div className="bg-green-500 rounded-2xl rounded-br-sm px-3 py-2 max-w-xs">
                              <p className="text-sm text-white">
                                Je souhaite réserver une vidange à domicile
                              </p>
                              <div className="text-xs text-green-100 mt-1 flex items-center justify-end">
                                09:31 <CheckCircle2 className="w-3 h-3 ml-1" />
                              </div>
                            </div>
                          </div>

                          {/* Incoming message */}
                          <div className="flex space-x-2">
                            <div className="bg-white rounded-2xl rounded-bl-sm px-3 py-2 max-w-xs shadow-sm">
                              <p className="text-sm text-gray-800">
                                Parfait ! 🚗 Quel est votre quartier ?
                              </p>
                              <div className="text-xs text-gray-500 mt-1">09:31</div>
                            </div>
                          </div>

                          {/* Typing indicator */}
                          <div className="flex space-x-2">
                            <div className="bg-white rounded-2xl rounded-bl-sm px-3 py-2 shadow-sm">
                              <div className="flex space-x-1">
                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Floating elements */}
                    <div className="absolute -top-4 -right-4 bg-white rounded-xl p-3 shadow-lg border animate-float">
                      <div className="text-sm font-semibold text-green-600">&lt; 5min</div>
                      <div className="text-xs text-gray-600">Réponse moyenne</div>
                    </div>

                    <div className="absolute -bottom-4 -left-4 bg-white rounded-xl p-3 shadow-lg border animate-float-delayed">
                      <div className="text-sm font-semibold text-blue-600">24/7</div>
                      <div className="text-xs text-gray-600">Disponible</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Structured Data */}
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPoint",
            "contactType": "Customer Service",
            "availableLanguage": ["French", "Arabic"],
            "areaServed": {
              "@type": "City",
              "name": "Casablanca",
              "addressCountry": "MA"
            },
            "hoursAvailable": {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": [
                "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"
              ],
              "opens": "08:00",
              "closes": "18:00"
            },
            "serviceChannel": {
              "@type": "ServiceChannel",
              "serviceType": "WhatsApp",
              "serviceUrl": "https://wa.me/212702094593"
            }
          })
        }}
      />

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
        
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-6px); }
        }
        
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        
        .animate-float-delayed {
          animation: float-delayed 3s ease-in-out infinite 1.5s;
        }
      `}</style>
    </section>
  );
};

export default WhatsAppSection; 